import { Box, Flex, Table, Tbody, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@morressier/alembic';
import { faPlus, faTags } from '@fortawesome/pro-regular-svg-icons';
import { fetchWorkspaceTags } from 'src/api/queries';
import DeleteTag from './DeleteTag';
import { useState } from 'react';
import CreateTag from './CreateTag';
import { useQuery } from '@tanstack/react-query';
import EditTag from './EditTag';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import { toTitleCase } from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import TagTableRow, {
  TagTableHeaderRow,
  TagTableSkeletonRows,
  getTagTableColumns
} from './TagTableRow';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

export type DeleteUsage = 'users' | 'submissions';
type DeleteTagType = {
  id: string;
  usage: DeleteUsage;
};

export type ITagFilter = {
  usage: string;
};

const ManageTags = ({ usage }: { usage: string }) => {
  const [deleteTag, setDeleteTag] = useState<DeleteTagType>({
    id: '',
    usage: 'submissions'
  });
  const [showEditTagModal, setShowEditTagModal] = useState({
    isOpen: false,
    tagId: '',
    name: '',
    description: '',
    usage: '',
    submissionContextId: '',
    tagCategoryId: '',
    tagCategory: '',
    tagColor: '',
    tagDescription: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const { workspaceId } = useWorkspace();

  const opeDeleteTagModal = (id: string, usage: DeleteUsage) => {
    setDeleteTag({ id, usage });
  };

  const openEditTagModal = (
    id: string,
    name: string,
    description: string,
    usage: string,
    submissionContextId: string,
    tagCategoryId: string,
    tagCategory: string,
    tagColor: string,
    tagDescription: string
  ) => {
    setShowEditTagModal({
      isOpen: true,
      tagId: id,
      name,
      description,
      usage: usage,
      submissionContextId: submissionContextId || '',
      tagCategoryId: tagCategoryId || '',
      tagCategory: tagCategory || '',
      tagColor: tagColor || '',
      tagDescription: tagDescription || ''
    });
  };
  const closeEditTagModal = () => {
    setShowEditTagModal({
      isOpen: false,
      tagId: '',
      name: '',
      description: '',
      usage: '',
      submissionContextId: '',
      tagCategoryId: '',
      tagCategory: '',
      tagColor: '',
      tagDescription: ''
    });
  };
  const offset = (page - 1) * pageSize;
  const { data: manageTags, isFetching: isLoading } = useQuery({
    queryKey: ['workspace-tags', usage, workspaceId, page, pageSize],
    queryFn: () =>
      workspaceId
        ? fetchWorkspaceTags(workspaceId, usage, offset, pageSize)
        : null,
    keepPreviousData: true,
    onSuccess: data => {
      const nextTotalPages = data?.total ? Math.ceil(data.total / pageSize) : 0;

      if (nextTotalPages > 0 && page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    }
  });

  const closeDeleteModal = () => setDeleteTag({ id: '', usage: 'submissions' });

  const totalTags = manageTags?.total || 0;
  const startIndex = totalTags > 0 ? offset + 1 : 0;
  const endIndex = Math.min(
    offset + (manageTags?.collection.length || 0),
    totalTags
  );

  const activeTabLabel = usage?.slice(0, -1);

  const isEmpty = manageTags?.total === 0;

  const createTagButton = (
    <Button
      iconLeft={<FontAwesomeIcon icon={faPlus} />}
      onClick={() => setShowCreateModal(true)}
    >
      Create Tag
    </Button>
  );

  return (
    <>
      {deleteTag.id ? (
        <DeleteTag
          deleteTagId={deleteTag.id}
          usage={deleteTag.usage}
          onClose={closeDeleteModal}
          workspaceId={workspaceId}
        />
      ) : null}
      {showCreateModal ? (
        <CreateTag
          onClose={() => setShowCreateModal(false)}
          workspaceId={workspaceId}
          usage={usage}
        />
      ) : null}
      {showEditTagModal.isOpen ? (
        <EditTag
          onClose={closeEditTagModal}
          defaultValue={showEditTagModal}
          workspaceId={workspaceId}
          usage={usage}
        />
      ) : null}

      <Flex
        alignItems={{ base: 'flex-start', md: 'flex-end' }}
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        width="100%"
        gap={4}
        mb={4}
      >
        {/* Left side — title + back button */}

        <Box marginBottom={4}>
          {isLoading ? (
            <Text>Searching for matches...</Text>
          ) : (
            <Flex alignItems={'center'} gap={2} height={5}>
              <Text textStyle="textBody">
                {totalTags > 0 ? (
                  <>
                    Showing <strong>{startIndex}</strong> –{' '}
                    <strong>{endIndex}</strong> of <strong>{totalTags}</strong>{' '}
                    {activeTabLabel} tag
                    {totalTags === 1 ? '' : 's'}
                  </>
                ) : (
                  <>
                    <strong>0</strong> {activeTabLabel} tags
                  </>
                )}
              </Text>
            </Flex>
          )}
        </Box>

        {/* Right side — FilterDrawer + Button */}
        <Flex alignItems="center" gap={4}>
          {createTagButton}
        </Flex>
      </Flex>

      <Box width="100%" marginTop={2} overflowX="auto">
        {isEmpty ? (
          <Flex flexDirection={'column'} gap={2} alignItems="center">
            <Flex
              width={'100%'}
              padding={8}
              justifyContent="center"
              background="neutralSecondaryFill"
              borderRadius="lg"
              fontSize="48px"
            >
              <FontAwesomeIcon icon={faTags} />
            </Flex>
            <Flex
              flexDirection={'column'}
              gap={2}
              alignItems="center"
              marginY={4}
            >
              <Text textStyle="headingGroup">
                No {toTitleCase(activeTabLabel)} Tag Found
              </Text>
              <Text textStyle="textBody">
                There are no {activeTabLabel} tags available yet. Once
                categories are added, they will appear here.
              </Text>
            </Flex>
            {createTagButton}
          </Flex>
        ) : (
          <Table
            size="sm"
            minWidth={{ base: '900px', md: '100%' }}
            sx={{ tableLayout: 'fixed' }}
          >
            <TagTableHeaderRow usage={usage} />
            <Tbody>
              {!manageTags ? (
                <TagTableSkeletonRows
                  columnCount={getTagTableColumns(usage).length}
                />
              ) : (
                manageTags.collection.map(tag => (
                  <TagTableRow
                    key={tag.id}
                    tagData={tag}
                    opeDeleteTagModal={opeDeleteTagModal}
                    openEditTagModal={openEditTagModal}
                  />
                ))
              )}
            </Tbody>
          </Table>
        )}
        <TablePagination
          ariaLabel="Tags pagination"
          currentPage={page}
          totalItems={totalTags}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Box>
    </>
  );
};

export default ManageTags;
